import React from "react";
import styled from "styled-components";

// 테마별 컬러 매핑 객체
const gaugeThemeColors = {
  dark: {
    gaugeGoodColor: "#46FC66",
    gaugeGoodBackground: "rgba(70, 252, 102, 0.30)",
    gaugeNormalColor: "#F9EA2A",
    gaugeNormalBackground: "rgba(249, 234, 42, 0.30)",
    gaugeBadColor: "#FF4400",
    gaugeBadBackground: "rgba(255, 68, 0, 0.30)",
    gaugeDefaultColor: "#CCCCCC",
    gaugeDefaultBackground: "rgba(204, 204, 204, 0.30)",
    gaugeTextColor: "white",
    gaugeBackgroundStroke: "rgba(255, 255, 255, 0.3)",
  },
  light: {
    gaugeGoodColor: "#00E64D",
    gaugeGoodBackground: "#00E64D",
    gaugeNormalColor: "#FFCC00",
    gaugeNormalBackground: "#FFCC00",
    gaugeBadColor: "#F94200",
    gaugeBadBackground: "#F94200",
    gaugeDefaultColor: "#ffffff",
    gaugeDefaultBackground: "rgba(255, 255, 255, 1)",
    gaugeTextColor: "black",
    gaugeBackgroundStroke: "rgba(0, 0, 0, 0.2)",
  },
};

/**
 * 테마모드에 따른 게이지 색상을 가져오는 함수
 * @param {string} colorName - 색상 이름
 * @param {string} themeMode - 테마 모드 ("dark" 또는 "light")
 * @returns {string} 해당 테마의 색상 값
 */
const getGaugeThemeColor = (colorName, themeMode = "dark") => {
  const theme = gaugeThemeColors[themeMode] || gaugeThemeColors.dark;
  return theme[colorName] || gaugeThemeColors.dark[colorName];
};

const AtomsGauge = ({
  value,
  unit,
  label,
  minValue = 0,
  maxValue = 100,
  goodRange = null, // [min, max] 형태로 좋음 범위 지정
  normalRange = null, // [min, max] 형태로 보통 범위 지정
  showStatus = true,
  status = null, // 수동으로 상태 지정하고 싶을 때
  themeMode = "dark", // 테마 모드 추가
}) => {
  // 값에 따른 상태 자동 결정 (범위는 props로 받을 수 있도록)
  const getStatusFromValue = (value, goodRange, normalRange) => {
    const numValue = parseFloat(value);

    if (isNaN(numValue)) return null;

    if (goodRange && numValue >= goodRange[0] && numValue <= goodRange[1]) {
      return "good";
    } else if (
      normalRange &&
      numValue >= normalRange[0] &&
      numValue <= normalRange[1]
    ) {
      return "normal";
    } else {
      return "bad";
    }
  };

  // 상태별 색상 설정 - 라벨별 텍스트 적용
  const getStatusConfig = (status, themeMode, label) => {
    const getStatusText = (status, label) => {
      switch (label) {
        case "온도":
          switch (status) {
            case "normal":
              return "낮음";
            case "good":
              return "적정";
            case "bad":
              return "높음";
            default:
              return null;
          }
        case "오존":
          switch (status) {
            case "good":
              return "낮음";
            case "normal":
              return "보통";
            case "bad":
              return "높음";
            default:
              return null;
          }
        case "습도":
          switch (status) {
            case "good":
              return "적정";
            case "normal":
              return "낮음";
            case "bad":
              return "높음";
            default:
              return null;
          }
        case "VOC":
          switch (status) {
            case "good":
              return "낮음";
            case "normal":
              return "보통";
            case "bad":
              return "높음";
            default:
              return null;
          }
        case "이산화탄소":
          switch (status) {
            case "good":
              return "적정";
            case "normal":
              return "보통";
            case "bad":
              return "나쁨";
            default:
              return null;
          }
        default:
          switch (status) {
            case "good":
              return "좋음";
            case "normal":
              return "보통";
            case "bad":
              return "나쁨";
            default:
              return null;
          }
      }
    };

    switch (status) {
      case "good":
        return {
          color: getGaugeThemeColor("gaugeGoodColor", themeMode),
          text: getStatusText(status, label),
          backgroundColor: getGaugeThemeColor("gaugeGoodBackground", themeMode),
        };
      case "normal":
        return {
          color: getGaugeThemeColor("gaugeNormalColor", themeMode),
          text: getStatusText(status, label),
          backgroundColor: getGaugeThemeColor(
            "gaugeNormalBackground",
            themeMode
          ),
        };
      case "bad":
        return {
          color: getGaugeThemeColor("gaugeBadColor", themeMode),
          text: getStatusText(status, label),
          backgroundColor: getGaugeThemeColor("gaugeBadBackground", themeMode),
        };
      default:
        return {
          color: getGaugeThemeColor("gaugeDefaultColor", themeMode),
          text: null,
          backgroundColor: getGaugeThemeColor(
            "gaugeDefaultBackground",
            themeMode
          ),
        };
    }
  };

  // 값을 퍼센티지로 변환 (0-100%)
  const getPercentageFromValue = (value, minValue = 0, maxValue = 100) => {
    const numValue = parseFloat(value);

    if (isNaN(numValue)) return 0;

    // 값을 0-100% 범위로 정규화
    const percentage = Math.max(
      0,
      Math.min(100, ((numValue - minValue) / (maxValue - minValue)) * 100)
    );

    return percentage;
  };

  // 상태 결정: 수동 지정 > 범위 기반 판정 (props로 받은 범위 사용)
  const finalStatus =
    status || getStatusFromValue(value, goodRange, normalRange);
  const config = getStatusConfig(finalStatus, themeMode, label);

  // 값에 따른 게이지 퍼센티지 계산
  const percentage = getPercentageFromValue(value, minValue, maxValue);

  // 반원 게이지를 위한 SVG path 계산
  const radius = 80;
  const circumference = Math.PI * radius * 1.1; // 반원이므로 π * r, 조금 늘려서 끝까지 가도록

  return (
    <StyledContainer>
      <StyledLabel themeMode={themeMode}>{label}</StyledLabel>

      <StyledGaugeContainer>
        <StyledSVG viewBox="0 0 220 110">
          {/* 배경 반원 - 하단 좌측에서 우측으로 */}
          <StyledBackgroundPath
            d={`M 22 88 A ${radius} ${radius} 0 0 1 198 88`}
            themeMode={themeMode}
          />
          {/* 값에 따른 채움 반원 - 하단 좌측에서 시작 */}
          <StyledFillPath
            d={`M 22 88 A ${radius} ${radius} 0 0 1 198 88`}
            color={config.color}
            percentage={percentage}
            circumference={circumference}
          />
        </StyledSVG>

        <StyledValueContainer>
          <StyledValue themeMode={themeMode}>{value}</StyledValue>
          <StyledUnit themeMode={themeMode}>{unit}</StyledUnit>
        </StyledValueContainer>
      </StyledGaugeContainer>

      {showStatus && config.text && (
        <StyledStatusBadge
          backgroundColor={config.backgroundColor}
          color={config.color}
          show={true}
        >
          <StyledStatusText themeMode={themeMode}>
            {config.text}
          </StyledStatusText>
        </StyledStatusBadge>
      )}
    </StyledContainer>
  );
};

export default AtomsGauge;

const StyledContainer = styled.div`
  width: 23vh;
  height: 26vh;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2.3vh;
`;

const StyledLabel = styled.span`
  color: ${(props) => getGaugeThemeColor("gaugeTextColor", props.themeMode)};
  font-size: 3.5vh;
  font-family: Poppins;
  font-weight: 600;
  line-height: 3.5vh;
  text-align: center;
  margin-bottom: 1.8vh;
`;

const StyledGaugeContainer = styled.div`
  width: 20vh;
  height: 12vh;
  position: relative;
  margin-top: -2.2vh;
`;

// SVG를 사용한 반원 게이지
const StyledSVG = styled.svg`
  width: 100%;
  height: 100%;
`;

const StyledBackgroundPath = styled.path`
  fill: none;
  stroke: ${(props) =>
    getGaugeThemeColor("gaugeBackgroundStroke", props.themeMode)};
  stroke-width: 22;
  stroke-linecap: round;
`;

const StyledFillPath = styled.path`
  fill: none;
  stroke: ${(props) => props.color};
  stroke-width: 22;
  stroke-linecap: round;
  stroke-dasharray: ${(props) => props.circumference};
  stroke-dashoffset: ${(props) =>
    props.circumference - (props.circumference * props.percentage) / 100};
  transition: stroke-dashoffset 0.3s ease-in-out;
  opacity: ${(props) => (props.percentage === 0 ? 0 : 1)};
`;

const StyledValueContainer = styled.div`
  position: absolute;
  top: calc(60% + 1.8vh);
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6vh;
`;

const StyledValue = styled.span`
  color: ${(props) => getGaugeThemeColor("gaugeTextColor", props.themeMode)};
  font-size: 4.7vh;
  font-family: Poppins;
  font-weight: 500;
  line-height: 4.7vh;
  text-align: center;
  word-wrap: break-word;
`;

const StyledUnit = styled.span`
  color: ${(props) => getGaugeThemeColor("gaugeTextColor", props.themeMode)};
  font-size: 2.4vh;
  font-family: Poppins;
  font-weight: 500;
  line-height: 2.4vh;
  text-align: center;
  word-wrap: break-word;
`;

const StyledStatusBadge = styled.div`
  width: 7.4vh;
  height: 4.8vh;
  background: ${(props) => props.backgroundColor};
  border-radius: 2.4vh;
  outline: 0.1vh ${(props) => props.color} solid;
  display: ${(props) => (props.show ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  margin-top: 0.9vh;
`;

const StyledStatusText = styled.span`
  color: ${(props) => getGaugeThemeColor("gaugeTextColor", props.themeMode)};
  font-size: 2.2vh;
  font-family: Poppins;
  font-weight: 500;
  line-height: 2.2vh;
`;
